// defaults to localStorage for web
import { PreloadedState, configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { FLUSH, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE, persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { middlewares } from "./middlewares";
import rootReducer from "./rootReducer";

const persistConfig = {
  key: "root",
  storage,
  // Removed "auth" from whitelist - tokens are handled by Kinde, not persisted in localStorage
  // Added "operator" to persist active operator across logout and page refreshes
  // Added "subscriptionAssignment" to persist welcome modal and plan selection state
  // Added "announcementTour" so a dismissed announcement stays dismissed across sessions — without
  // it a "show once" tour is really "show once per page load", which is the whole feature.
  whitelist: ["config", "quickSetup", "operator", "subscriptionAssignment", "faView", "announcementTour"],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(middlewares),
  devTools: import.meta.env.DEV,
});

export const persistor = persistStore(store);

export const setupStore = (preloadedState?: PreloadedState<RootState>) => {
  return configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(middlewares),
    preloadedState,
  });
};

// optional, but required for refetchOnFocus/refetchOnReconnect behaviors
// see `setupListeners` docs - takes an optional callback as the 2nd arg for customization
setupListeners(store.dispatch);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = typeof store.dispatch;
